// 11. 10. 2026 (owner, docs/marketplace.md): the Market. Players sell gear and Blueprints to each other for Relic Shards,
// at a fixed price: the first buyer who pays gets it. A listing lasts 48 hours and costs a fee in Scrap (never given
// back); 10% of every sale is destroyed (the main drain of Relic Shards); the price stays between a floor and a ceiling
// by tier and rarity (a Legendary's ceiling is about what the Relic Forge asks). A bought item waits 24 hours before it
// can be listed again, and a Legendary changes owners at most 3 times ("Owners 2/3"). It opens on level 10 with an
// account older than 7 days, and only while the server's `market` flag is on (the owner opens it at about 50 players a
// day). Everything here is pure: the server (game/market.ts) keeps the listings and checks every rule again.

import { z } from 'zod';
import { AFFIX_POOLS } from './content/items';
import { affixQuality } from './itemgen';
import { RELIC_RULES } from './items/relics';
import { GEAR_SLOTS, RARITIES, WEAPON_CLASSES, type GearSlot, type ItemInstance, type ItemStatKey, type Rarity, type WeaponClass } from './types';
import type { Blueprint } from './items/gearRecipes';
import type { ItemDTO } from './api';

export const MARKET_RULES = {
  /** The survivor's level that opens it, and how old the account must be (days; 28. 10. 2026, owner: 5, was 7). */
  level: 10,
  accountDays: 5,
  /** A listing's life. */
  listingHours: 48,
  /** Active listings a player can have at once. */
  maxActive: 5,
  /** Share of every sale that is destroyed (rounded up: every sale destroys at least 1 shard). */
  tax: 0.1,
  /** Sales a seller can make in one of their days (the contract board's day); the rest of their listings wait. */
  dailySales: 3,
  /** A bought item cannot be listed again for this long. */
  boughtLockHours: 24,
  /** A Legendary changes owners at most this many times (the first owner counts). */
  legendaryOwners: 3,
  /** Price history: the average of the sales in the last 7 days. */
  historyDays: 7,
  /** Suspicious pairs (pilot report): the same buyer and seller this many times within `pairDays`. */
  pairSales: 2,
  pairDays: 7,
};

export const MARKET_CURRENCY = 'relic_shard' as const;

/**
 * Price floor and ceiling in Relic Shards, by rarity and tier (T1–T4). The floors stop gifts between a player's own
 * accounts (an Epic's floor is above the 1–2 shards it gives when dismantled, so buying to dismantle never pays); the
 * ceilings stop shards moving for nothing. A Legendary's top is the Relic Forge's price (60): a found Legendary of the
 * survivor's tier is never dearer than forging one. A Blueprint has the range of an item of its rarity and tier.
 */
export const MARKET_PRICES: Record<Rarity, readonly [number, number][]> = {
  common: [[2, 3], [2, 3], [2, 4], [2, 4]],
  uncommon: [[2, 4], [2, 5], [3, 6], [3, 8]],
  rare: [[3, 8], [3, 10], [4, 12], [5, 15]],
  epic: [[4, 15], [5, 18], [6, 22], [8, 28]],
  legendary: [[20, 40], [25, 48], [30, 54], [35, RELIC_RULES.forgeCost]],
};

/** The listing fee in Scrap by rarity (paid when listing, never given back). */
export const MARKET_FEE: Record<Rarity, number> = { common: 10, uncommon: 15, rare: 25, epic: 50, legendary: 100 };

const clampTier = (tier: number) => Math.max(1, Math.min(4, Math.round(tier || 1))) as 1 | 2 | 3 | 4;

export function marketPriceRange(rarity: Rarity, tier: number): { floor: number; ceiling: number } {
  const [floor, ceiling] = MARKET_PRICES[rarity][clampTier(tier) - 1];
  return { floor, ceiling };
}

export const marketFee = (rarity: Rarity): number => MARKET_FEE[rarity];

/** The tax on a sale: 10% of the price, rounded up. */
export const marketTax = (price: number): number => Math.max(0, Math.ceil(price * MARKET_RULES.tax - 1e-9));

/** What the seller gets for a sale. */
export const sellerGets = (price: number): number => price - marketTax(price);

/** A price the seller may ask: whole, inside the item's range. */
export function marketPriceProblem(price: number, rarity: Rarity, tier: number): 'low' | 'high' | 'whole' | null {
  if (!Number.isInteger(price)) return 'whole';
  const r = marketPriceRange(rarity, tier);
  if (price < r.floor) return 'low';
  if (price > r.ceiling) return 'high';
  return null;
}

// ---- Who can trade ------------------------------------------------------------------------------------

export type MarketLock = 'off' | 'level' | 'age';

/**
 * Whether the Market is open for a survivor: the server's flag, level 10, and an account older than 7 days.
 * 11. 10. 2026 (owner): the ACCOUNT's age (`users.created_at`), never the survivor's: the survivor is made again every
 * season. `opensAt`: when the age is met.
 */
export function marketAccess(o: { flag: boolean; level: number; accountAt: number }, now: number): { open: boolean; lock: MarketLock | null; opensAt: number | null } {
  const opensAt = o.accountAt + MARKET_RULES.accountDays * 86_400_000;
  if (!o.flag) return { open: false, lock: 'off', opensAt: null };
  if (o.level < MARKET_RULES.level) return { open: false, lock: 'level', opensAt: now >= opensAt ? null : opensAt };
  if (now < opensAt) return { open: false, lock: 'age', opensAt };
  return { open: true, lock: null, opensAt: null };
}

/**
 * 11. 10. 2026 (owner): when the account was made, for the checks that have only the survivor: the time the server kept
 * in its onboarding (`accountAt`, from `users.created_at`), else the survivor's own start (never earlier than the
 * account's, so never opens too soon).
 */
export const accountStartOf = (s: { onboarding?: { accountAt?: number } | null; createdAt: Date }): number => s.onboarding?.accountAt ?? s.createdAt.getTime();

// ---- What can be listed ("what is kept", the same rules as the Sell tab's) ------------------------------

/**
 * Why an item stays (cannot be listed). The Sell tab at Tobias keeps what the next runs take; the Market keeps, in the
 * same spirit, what the survivor wears or protects: worn, not in Storage (the pack, a Workshop bench), locked, bound
 * (Relic Forge), taken in a fight (24 h custody), bought less than 24 h ago, a Legendary with its 3 owners, in a
 * Workshop job, in the arena set (The Pit, at merge).
 */
export type MarketBlock = 'equipped' | 'not_storage' | 'locked' | 'account_bound' | 'taken' | 'bought' | 'owners' | 'busy' | 'arena';

export const MARKET_BLOCK_ORDER: readonly MarketBlock[] = ['equipped', 'not_storage', 'busy', 'locked', 'account_bound', 'taken', 'bought', 'owners', 'arena'];

export interface MarketListable {
  location: string;
  locked: boolean;
  accountBound: boolean;
  rarity: Rarity;
  /** Owners so far (1: the one who found or made it). */
  owners?: number | null;
  /** Package 4, part E: held after a fight until then. */
  custodyUntil?: number | null;
  /** When the current owner bought it on the Market (null: never). */
  boughtAt?: number | null;
  /** In a Workshop job (an upgrade, a tier, a Rework, a repair). */
  busy?: boolean;
  /** In the arena set (The Pit; filled at merge). */
  arena?: boolean;
}

/**
 * The one check of what can be listed (server and screen): the reason it stays, with until when for the ones that end
 * (taken, bought), or null when it can go on the Market.
 */
export function marketListBlock(it: MarketListable, now: number): { reason: MarketBlock; until?: number } | null {
  if (it.location === 'equipped') return { reason: 'equipped' };
  if (it.location !== 'storage') return { reason: 'not_storage' };
  if (it.busy) return { reason: 'busy' };
  if (it.locked) return { reason: 'locked' };
  if (it.accountBound) return { reason: 'account_bound' };
  if (it.custodyUntil != null && now < it.custodyUntil) return { reason: 'taken', until: it.custodyUntil };
  const boughtUntil = it.boughtAt != null ? it.boughtAt + MARKET_RULES.boughtLockHours * 3_600_000 : null;
  if (boughtUntil != null && now < boughtUntil) return { reason: 'bought', until: boughtUntil };
  if (it.rarity === 'legendary' && (it.owners ?? 1) >= MARKET_RULES.legendaryOwners) return { reason: 'owners' };
  if (it.arena) return { reason: 'arena' };
  return null;
}

/** "Owners 2/3": a Legendary's owners so far (null for the other rarities). */
export function ownersLine(rarity: Rarity, owners: number | null | undefined): { n: number; max: number } | null {
  return rarity === 'legendary' ? { n: Math.max(1, owners ?? 1), max: MARKET_RULES.legendaryOwners } : null;
}

// ---- Search -----------------------------------------------------------------------------------------------

export const MARKET_SORTS = ['price_asc', 'price_desc', 'power_desc', 'power_asc', 'new'] as const;
export type MarketSort = (typeof MARKET_SORTS)[number];

/** Every stat an affix can be (the Affix filter), in the pools' order. Recalibrated affixes are affixes too. */
export const MARKET_AFFIXES: ItemStatKey[] = [...new Set(GEAR_SLOTS.flatMap((s) => AFFIX_POOLS[s].map(([k]) => k)))];

export interface MarketFilter {
  kind?: 'item' | 'blueprint';
  slot?: GearSlot;
  tier?: 1 | 2 | 3 | 4;
  rarity?: Rarity;
  weaponClass?: WeaponClass;
  /** An affix the item must have, and how well it rolled at least (0–1; its place in its range, as the item detail). */
  affix?: ItemStatKey;
  minRoll?: number;
  sort?: MarketSort;
}

export const marketFilterSchema = z.object({
  kind: z.enum(['item', 'blueprint']).optional(),
  slot: z.enum(GEAR_SLOTS).optional(),
  tier: z.coerce.number().int().min(1).max(4).optional(),
  rarity: z.enum(RARITIES).optional(),
  weaponClass: z.enum(WEAPON_CLASSES).optional(),
  affix: z.string().max(30).optional(),
  minRoll: z.coerce.number().min(0).max(1).optional(),
  sort: z.enum(MARKET_SORTS).default('price_asc'),
});
export const marketListSchema = z.union([
  z.object({ itemId: z.string().min(1).max(80), price: z.number().int().min(1).max(1000) }),
  z.object({ blueprintId: z.string().min(1).max(80), price: z.number().int().min(1).max(1000) }),
]);

/** What a search looks at in a listing. */
export interface MarketSearchRow {
  kind: 'item' | 'blueprint';
  slot: GearSlot | null;
  tier: number;
  rarity: Rarity;
  weaponClass: WeaponClass | null;
  price: number;
  power: number;
  createdAt: number;
  item: Pick<ItemInstance, 'templateId' | 'ilvl' | 'rarity' | 'tier' | 'affixes'> | null;
}

/** Whether a listing matches the filters (a Blueprint has no slot, class or affix: those filters leave it out). */
export function marketMatches(r: MarketSearchRow, f: MarketFilter): boolean {
  if (f.kind && r.kind !== f.kind) return false;
  if (f.tier && r.tier !== f.tier) return false;
  if (f.rarity && r.rarity !== f.rarity) return false;
  if (f.slot && r.slot !== f.slot) return false;
  if (f.weaponClass && r.weaponClass !== f.weaponClass) return false;
  if (f.affix || f.minRoll) {
    if (!r.item) return false;
    const affixes = r.item.affixes.filter((a) => !f.affix || a.stat === f.affix);
    if (!affixes.length) return false;
    if (f.minRoll && !affixes.some((a) => (affixQuality(r.item!, a) ?? 0) >= f.minRoll! - 1e-9)) return false;
  }
  return true;
}

export function marketCompare(sort: MarketSort = 'price_asc'): (a: MarketSearchRow, b: MarketSearchRow) => number {
  const newest = (a: MarketSearchRow, b: MarketSearchRow) => b.createdAt - a.createdAt;
  switch (sort) {
    case 'price_desc':
      return (a, b) => b.price - a.price || newest(a, b);
    case 'power_desc':
      return (a, b) => b.power - a.power || a.price - b.price;
    case 'power_asc':
      return (a, b) => a.power - b.power || a.price - b.price;
    case 'new':
      return newest;
    default:
      return (a, b) => a.price - b.price || newest(a, b);
  }
}

// ---- Price history ------------------------------------------------------------------------------------------

/** The kind of a listing for its price history: an item by its template and rarity, a Blueprint by rarity and tier. */
export function marketKindKey(o: { kind: 'item' | 'blueprint'; templateId?: string | null; rarity: Rarity; tier: number }): string {
  return o.kind === 'blueprint' ? `bp:${o.rarity}:${o.tier}` : `${o.templateId}:${o.rarity}`;
}

export interface PriceHistory {
  /** The average price of the sales in the last 7 days (null: none). */
  avg: number | null;
  n: number;
  /** Day by day (UTC), oldest first, 7 days. */
  days: { day: string; avg: number | null; n: number }[];
}

export function priceHistory(sales: { price: number; at: number }[], now: number, days = MARKET_RULES.historyDays): PriceHistory {
  const DAY = 86_400_000;
  const from = now - days * DAY;
  const recent = sales.filter((s) => s.at > from && s.at <= now);
  const out: PriceHistory['days'] = [];
  for (let i = days - 1; i >= 0; i--) {
    const day = new Date(now - i * DAY).toISOString().slice(0, 10);
    const in_ = recent.filter((s) => new Date(s.at).toISOString().slice(0, 10) === day);
    out.push({ day, avg: in_.length ? Math.round((in_.reduce((a, s) => a + s.price, 0) / in_.length) * 10) / 10 : null, n: in_.length });
  }
  const avg = recent.length ? Math.round((recent.reduce((a, s) => a + s.price, 0) / recent.length) * 10) / 10 : null;
  return { avg, n: recent.length, days: out };
}

// ---- Suspicious sales (pilot report, admin) --------------------------------------------------------------

/** `pair`: the same buyer and seller again within 7 days; `floor` / `ceiling`: a price at the edge of its range. */
export type MarketFlag = 'pair' | 'floor' | 'ceiling';

export function saleFlags(o: { price: number; rarity: Rarity; tier: number; pairSales: number }): MarketFlag[] {
  const r = marketPriceRange(o.rarity, o.tier);
  const out: MarketFlag[] = [];
  if (o.pairSales >= MARKET_RULES.pairSales) out.push('pair');
  if (o.price <= r.floor) out.push('floor');
  if (o.price >= r.ceiling) out.push('ceiling');
  return out;
}

// ---- What the screens get ----------------------------------------------------------------------------------

export type MarketStatus = 'active' | 'sold' | 'expired' | 'canceled' | 'reversed' | 'season';

export interface MarketBlueprintView extends Pick<Blueprint, 'rarity' | 'tier' | 'zone'> {
  name: string;
  zoneName: string | null;
  /** Items it can teach (template ids). */
  pool: string[];
}

export interface MarketListingDTO {
  id: string;
  kind: 'item' | 'blueprint';
  /** The seller's name; `crew`: the crew's tag (from the crews package, at merge), null without one. */
  seller: { name: string; crew: string | null };
  mine: boolean;
  price: number;
  rarity: Rarity;
  tier: number;
  slot: GearSlot | null;
  weaponClass: WeaponClass | null;
  power: number;
  item: ItemDTO | null;
  blueprint: MarketBlueprintView | null;
  /** A Legendary's owners so far ("Owners 2/3"); null for the others. */
  owners: { n: number; max: number } | null;
  /** 11. 10. 2026 (owner): a broken item may be listed, and says so (its durability is in `item`). */
  broken: boolean;
  createdAt: number;
  expiresAt: number;
  /** The kind's price over the last 7 days. */
  avg7: number | null;
  sales7: number;
}

export interface MarketDTO {
  open: boolean;
  lock: MarketLock | null;
  /** Level 10 reached and the account is young: when it opens. */
  opensAt: number | null;
  level: number;
  shards: number;
  scrap: number;
  rules: { tax: number; maxActive: number; listingHours: number; dailySales: number; legendaryOwners: number; boughtLockHours: number };
  listings: MarketListingDTO[];
  total: number;
  /** Your active listings and sales today. */
  active: number;
  soldToday: number;
}

export interface MyListingDTO extends MarketListingDTO {
  status: MarketStatus;
  closedAt: number | null;
  buyer: string | null;
  tax: number;
  /** What the seller got (price − tax), 0 unless sold. */
  got: number;
  fee: number;
}

export interface MarketMineDTO {
  active: MyListingDTO[];
  sold: MyListingDTO[];
  expired: MyListingDTO[];
  soldToday: number;
  dailySales: number;
  maxActive: number;
}

/** One item or Blueprint of Storage on the Sell tab: what it would take, or why it stays. */
export interface MarketSellRowDTO {
  kind: 'item' | 'blueprint';
  id: string;
  item: ItemDTO | null;
  blueprint: (MarketBlueprintView & { id: string }) | null;
  rarity: Rarity;
  tier: number;
  block: { reason: MarketBlock; until?: number } | null;
  /** 11. 10. 2026 (owner): a broken item (it may be listed, and says so). */
  broken: boolean;
  floor: number;
  ceiling: number;
  fee: number;
  avg7: number | null;
  sales7: number;
}

export interface MarketSellDTO {
  rows: MarketSellRowDTO[];
  active: number;
  maxActive: number;
  scrap: number;
  shards: number;
  tax: number;
  listingHours: number;
}

export interface MarketHistoryDTO extends PriceHistory {
  key: string;
}
